/**
 * Fatiado de pages/ImportMenu.tsx por responsabilidade (#131, R3).
 */
import { useRef, useState } from 'react'
import { imageToJpeg } from './prepare.js'
import { importsApi } from '../../lib/api.js'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { MenuImportView } from '../../lib/api.js'
import { useI18n } from '../../lib/i18n.js'
import { defaultPhotoPage, pendingPrompt, photoPageOptions } from '../../lib/import-prompt.js'
import { Button, Card, toast } from '../../components/ui/index.js'

// ─── Aguardando o dono (#300) ─────────────────────────────────────────────────

/** Import pausado em WAITING_INPUT: mostra a pergunta (ou um texto claro
 *  quando o servidor não mandou nenhum) e TODAS as saídas — responder,
 *  enviar nova foto, seguir sem a página, cancelar. */
export function AwaitingCard({
  view,
  onCancelled,
}: {
  view: MenuImportView
  onCancelled?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const qc = useQueryClient()
  const photoInput = useRef<HTMLInputElement>(null)
  const prompt = pendingPrompt(view)!
  const refresh = () => void qc.invalidateQueries({ queryKey: ['menu-import', view.id] })
  // #301: "Enviar nova foto" sempre disponível (também quando a pergunta é
  // texto livre). Página: a pedida pelo servidor; sem pedido, o dono escolhe
  // (padrão = primeira com erro de leitura) — nunca "página 1" às cegas.
  const pageOptions = photoPageOptions(view)
  const [chosenPage, setChosenPage] = useState<number | null>(null)
  const photoPage = prompt.page ?? chosenPage ?? defaultPhotoPage(view)

  const reply = useMutation({
    mutationFn: ({ text, skip = false }: { text: string; skip?: boolean }) =>
      importsApi.message(view.id, text, skip),
    onSuccess: refresh,
    onError: (e: Error) => toast.error(e.message),
  })
  const photo = useMutation({
    mutationFn: (blob: Blob) => importsApi.sendPage(view.id, photoPage, blob),
    onSuccess: () => {
      toast.success(t('admin.import.replaced_page', { n: String(photoPage) }))
      refresh()
    },
    onError: (e: Error) => toast.error(e.message),
  })
  const cancel = useMutation({
    mutationFn: () => importsApi.cancel(view.id),
    onSuccess: () => {
      refresh()
      onCancelled?.()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const text =
    prompt.text ??
    (prompt.kind === 'photo'
      ? t('admin.import.awaiting_photo_fallback', { n: String(photoPage) })
      : t('admin.import.awaiting_input_fallback'))
  const skipLabel =
    prompt.kind === 'photo' ? t('admin.import.awaiting_skip') : t('admin.import.awaiting_continue')
  // o quick reply do servidor já pode ser o "seguir sem" — não duplicar
  const replies = prompt.quickReplies.filter((q) => q.action === 'reply')
  const hasSkip = replies.some((q) => q.label === skipLabel)
  const busy = reply.isPending || photo.isPending || cancel.isPending

  return (
    <div data-testid="import-awaiting">
      <Card className="space-y-3 border-2 border-amber-300">
        <p className="text-sm font-bold text-gray-900">💬 {t('admin.import.awaiting_title')}</p>
        <p className="text-sm text-gray-800" data-testid="import-awaiting-text">
          {text}
        </p>
        <div className="flex flex-wrap gap-2">
          {replies.map((q, i) => (
            <button
              key={i}
              type="button"
              disabled={busy}
              data-testid="import-awaiting-reply"
              onClick={() => reply.mutate({ text: q.label, skip: q.label === skipLabel })}
              className="rounded-full border border-amber-300 bg-white text-gray-700 text-xs px-3 py-1.5 hover:bg-amber-100"
            >
              {q.label}
            </button>
          ))}
          {!hasSkip && (
            <button
              type="button"
              disabled={busy}
              data-testid="import-awaiting-skip"
              onClick={() => reply.mutate({ text: skipLabel, skip: true })}
              className="rounded-full border border-amber-300 bg-white text-gray-700 text-xs px-3 py-1.5 hover:bg-amber-100"
            >
              {skipLabel}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {prompt.page === null && pageOptions.length > 1 && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              {t('admin.import.awaiting_photo_page')}
              <select
                data-testid="import-awaiting-photo-page"
                value={photoPage}
                disabled={busy}
                onChange={(e) => setChosenPage(Number(e.target.value))}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
              >
                {pageOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          )}
          <Button
            data-testid="import-awaiting-photo"
            disabled={busy}
            loading={photo.isPending}
            onClick={() => photoInput.current?.click()}
          >
            📷 {t('admin.import.awaiting_send_photo')}
          </Button>
          <Button
            variant="secondary"
            data-testid="import-awaiting-cancel"
            disabled={busy}
            loading={cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            {t('admin.import.awaiting_cancel')}
          </Button>
        </div>
        <p className="text-xs text-gray-500">{t('admin.import.awaiting_hint')}</p>
        <input
          ref={photoInput}
          data-testid="import-awaiting-photo-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0]
            if (!file) return
            photo.mutate(await imageToJpeg(file))
            e.target.value = ''
          }}
        />
      </Card>
    </div>
  )
}
